import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Link } from "react-router-dom";
import { Dropdown, DropdownToggle, DropdownMenu, Row, Col } from "reactstrap";
import SimpleBar from "simplebar-react";
import { getExpiringDocumentsForNotification, getEmployees } from "../../Api/GmsEmployee";
import { getEmployeesDueForLeaveCreditNotification } from "../../Api/GmsAnnualLeaveCreditHistory";
import { getBookingsWithEmployeesOnLeave } from "../../Api/GmsBooking";

//i18n
import { withTranslation } from "react-i18next";
import {
  hasPagePermissionWithAdmin,
  isUserAdmin
} from "../../Permission/PermissionService";

const NotificationDropdown = props => {
  // Declare a new state variable, which we'll call "menu"
  const [menu, setMenu] = useState(false);
  const [expiringDocuments, setExpiringDocuments] = useState([]);
  const [leaveCreditNotifications, setLeaveCreditNotifications] = useState([]);
  const [bookingLeaveNotifications, setBookingLeaveNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadNotifications();
  }, []);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  
  const hasExpiringDocsPermission = hasPagePermissionWithAdmin(permissions, '/GmsExpiringDocuments') || isAdmin;
  const hasLeaveCreditsPermission = hasPagePermissionWithAdmin(permissions, '/GmsAnnualLeaveCredit') || isAdmin;
  const hasBookingConflictsPermission = hasPagePermissionWithAdmin(permissions, '/GmsBooking') || isAdmin;

  const loadNotifications = async () => {
    setLoading(true);
    try {
      // Load each notification type separately to handle errors individually
      try {
        const documents = await getExpiringDocumentsForNotification();
        setExpiringDocuments(documents);
      } catch (error) {
        console.error("❌ [NOTIFICATION] Error loading expiring documents:", error);
        setExpiringDocuments([]);
      }
      
      try {
        const leaveCredits = await getEmployeesDueForLeaveCreditNotification();
        setLeaveCreditNotifications(leaveCredits);
      } catch (error) {
        console.error("Error loading leave credits:", error);
        setLeaveCreditNotifications([]);
      }
      
      try {
        // Get user's companyId and branchId from localStorage
        const userCompanyId = localStorage.getItem("userCompanyId");
        const userBranchId = localStorage.getItem("userBranchId");
        
        // Build filter object - only include if values exist
        const filter = {};
        if (userCompanyId && userCompanyId !== 'null' && userCompanyId !== 'undefined') {
          filter.employee_Business_Id = parseInt(userCompanyId);
        }
        if (userBranchId && userBranchId !== 'null' && userBranchId !== 'undefined') {
          filter.employee_Branch_Id = parseInt(userBranchId);
        }
        
        // Get filtered employees to match against bookings
        const filteredEmployees = await getEmployees(filter);
        const filteredEmployeeIds = new Set(filteredEmployees.map(emp => emp.employee_Id));
        
        const bookingLeaves = await getBookingsWithEmployeesOnLeave();
        const bookings = bookingLeaves.data || [];
        
        // Filter bookings to only include those with employees matching company/branch filter
        // If no filter is set (companyId and branchId are null), show all bookings
        const filteredBookings = (userCompanyId || userBranchId) 
          ? bookings.filter(booking => filteredEmployeeIds.has(booking.employee_Id))
          : bookings;
        
        setBookingLeaveNotifications(filteredBookings);
      } catch (error) {
        console.error("Error loading booking leaves:", error);
        setBookingLeaveNotifications([]);
      }

    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getNotificationIcon = (documentType) => {
    switch (documentType) {
      case "Passport":
        return "bx bx-id-card";
      case "Visa":
        return "bx bx-plane";
      case "Nationality ID":
        return "bx bx-user-check";
      case "Labour Card":
        return "bx bx-briefcase";
      default:
        return "bx bx-file";
    }
  };

  const getNotificationColor = (isCritical) => {
    return isCritical ? "bg-danger" : "bg-warning";
  };

  const formatDaysLeft = (daysLeft) => {
    if (daysLeft < 0) {
      const daysAgo = Math.abs(daysLeft);
      return `Expired ${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`;
    }
    return `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`;
  };

  // Calculate total notifications based on permissions
  const totalNotifications = (hasExpiringDocsPermission ? expiringDocuments.length : 0) + 
                             (hasLeaveCreditsPermission ? leaveCreditNotifications.length : 0) + 
                             (hasBookingConflictsPermission ? bookingLeaveNotifications.length : 0);

  const hasCriticalNotifications = (hasExpiringDocsPermission && expiringDocuments.some(doc => doc.isCritical)) || 
                                   (hasLeaveCreditsPermission && leaveCreditNotifications.some(emp => emp.isCritical)) ||
                                   (hasBookingConflictsPermission && bookingLeaveNotifications.length > 0);


  return (
    <React.Fragment>
      <Dropdown
        isOpen={menu}
        toggle={() => setMenu(!menu)}
        className="dropdown d-inline-block"
        tag="li"
      >
        <DropdownToggle
          className="btn header-item noti-icon position-relative"
          tag="button"
          id="page-header-notifications-dropdown"
        >
          <i className="bx bx-bell bx-tada" />
          {totalNotifications > 0 && (
            <span className={`badge ${hasCriticalNotifications ? 'bg-danger' : 'bg-warning'} rounded-pill`}>
              {totalNotifications}
            </span>
          )}
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0" style={{ width: "380px" }}>
          <div className="p-3">
            <Row className="align-items-center">
              <Col>
                <h6 className="m-0"> {props.t("Notifications")} </h6>
              </Col>
              <div className="col-auto">
                <Link 
                  to="/GmsNotifications" 
                  className="small"
                  onClick={() => setMenu(false)}
                >
                  {" "}
                  View All
                </Link>
              </div>
            </Row>
          </div>

          <SimpleBar style={{ height: "300px" }}>
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 mb-0">Loading notifications...</p>
              </div>
            ) : totalNotifications === 0 ? (
              <div className="text-center py-4">
                <i className="bx bx-check-circle text-success" style={{ fontSize: "2rem" }}></i>
                <p className="mt-2 mb-0">No notifications</p>
              </div>
            ) : (
              <>
                {/* Expiring Documents Notifications */}
                {hasExpiringDocsPermission && expiringDocuments.slice(0, 3).map((doc, index) => (
                  <Link 
                    to="/GmsExpiringDocuments" 
                    className="text-reset notification-item" 
                    key={`doc-${index}`}
                    onClick={() => setMenu(false)}
                  >
                    <div className="d-flex">
                      <div className="avatar-xs me-3">
                        <span className={`avatar-title ${getNotificationColor(doc.isCritical)} rounded-circle font-size-16`}>
                          <i className={getNotificationIcon(doc.documentType)} />
                        </span>
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mt-0 mb-1">
                          {doc.employeeName} - {doc.documentType}
                        </h6>
                        <div className={`font-size-12 ${doc.daysLeft < 0 ? 'text-danger' : 'text-muted'}`}>
                          <p className="mb-1">
                            {doc.daysLeft < 0 ? 'Document expired on' : 'Document expires on'} {formatDate(doc.expiryDate)}
                          </p>
                          <p className={`mb-0 ${doc.daysLeft < 0 ? 'text-danger fw-bold' : ''}`}>
                            <i className="mdi mdi-clock-outline" />{" "}
                            {formatDaysLeft(doc.daysLeft)}
                            {doc.isCritical && doc.daysLeft >= 0 && (
                              <span className="badge bg-danger ms-2">Critical</span>
                            )}
                            {doc.daysLeft < 0 && (
                              <span className="badge bg-danger ms-2">Expired</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}

                {/* Leave Credit Notifications */}
                {hasLeaveCreditsPermission && leaveCreditNotifications.slice(0, 2).map((emp, index) => (
                  <Link 
                    to="/GmsAnnualLeaveCredit" 
                    className="text-reset notification-item" 
                    key={`credit-${index}`}
                    onClick={() => setMenu(false)}
                  >
                    <div className="d-flex">
                      <div className="avatar-xs me-3">
                        <span className={`avatar-title ${getNotificationColor(emp.isCritical)} rounded-circle font-size-16`}>
                          <i className="bx bx-calendar-check" />
                        </span>
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mt-0 mb-1">
                          {emp.employeeName} - Annual Leave Credit Due
                        </h6>
                        <div className="font-size-12 text-muted">
                          <p className="mb-1">
                            Last credit: {formatDate(emp.lastCreditDate)}
                          </p>
                          <p className="mb-0">
                            <i className="mdi mdi-clock-outline" />{" "}
                            {emp.daysOverdue} days overdue
                            {emp.isCritical && (
                              <span className="badge bg-danger ms-2">Critical</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}

                {/* Booking Leave Notifications */}
                {hasBookingConflictsPermission && bookingLeaveNotifications.slice(0, 3).map((booking, index) => (
                  <Link 
                    to="/GmsBooking" 
                    state={{ bookingId: booking.booking_Id, editMode: true }}
                    className="text-reset notification-item" 
                    key={`booking-${index}`}
                    onClick={() => setMenu(false)}
                  >
                    <div className="d-flex">
                      <div className="avatar-xs me-3">
                        <span className="avatar-title bg-danger rounded-circle font-size-16">
                          <i className="bx bx-calendar-x" />
                        </span>
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mt-0 mb-1">
                          {booking.employeeName} - {booking.customerName}
                        </h6>
                        <div className="font-size-12 text-muted">
                          <p className="mb-1">
                            Booking Date: {formatDate(booking.booking_Date)}
                          </p>
                          <p className="mb-0">
                            <i className="mdi mdi-calendar-range" />{" "}
                            Leave: {formatDate(booking.leave_FromDate)} to {formatDate(booking.leave_ToDate)}
                            <span className="badge bg-danger ms-2">On Leave</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </>
            )}
          </SimpleBar>
          
          {totalNotifications > (
            (hasExpiringDocsPermission ? Math.min(expiringDocuments.length, 3) : 0) + 
            (hasLeaveCreditsPermission ? Math.min(leaveCreditNotifications.length, 2) : 0) + 
            (hasBookingConflictsPermission ? Math.min(bookingLeaveNotifications.length, 3) : 0)
          ) && (
            <div className="p-2 border-top d-grid">
              <Link 
                className="btn btn-sm btn-link font-size-14 text-center" 
                to="/GmsNotifications"
                onClick={() => setMenu(false)}
              >
                <i className="mdi mdi-arrow-right-circle me-1"></i> 
                <span key="t-view-more">View More ({totalNotifications - 5} more)</span>
              </Link>
            </div>
          )}
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

NotificationDropdown.propTypes = {
  t: PropTypes.any
};

export default withTranslation()(NotificationDropdown);